import { useEffect, useState } from 'react';
import { workspaceApi } from '@/workspace/api';
import { knowledgePagesApi } from '@/workspace/knowledge-pages/api';
import type { ActionView } from './action-confirmation';
import type { ActionCopy } from './action-copy';
import { ActionDraft } from './action-draft';
import { DocumentDiff as DocumentDiffView } from '@/workspace/document-diff-view';
import { diffDocuments } from '@/workspace/document-diff';
import { documentDiffCopy } from '@/workspace/document-diff-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { createdPageMarkdown } from './page-suggestion-view';
import './document-diff.css';

type LoadedDiff = ReturnType<typeof diffDocuments>;

function snapshotText(snapshot: { name?: string; description?: string; instructions?: string }) {
  return [snapshot.name && `# ${snapshot.name}`, snapshot.description, snapshot.instructions]
    .filter(Boolean)
    .join('\n\n');
}

function actionText(action: ActionView) {
  const args = action.args;
  if (action.tool === 'save_work_result') {
    const payload = args.payload;
    return typeof payload === 'object' &&
      payload &&
      typeof (payload as { markdown?: unknown }).markdown === 'string'
      ? (payload as { markdown: string }).markdown
      : null;
  }
  if (action.tool === 'propose_knowledge_change')
    return typeof args.markdown === 'string' ? args.markdown : null;
  if (action.tool === 'update_skill_draft') return snapshotText(args);
  return null;
}

async function loadPrevious(action: ActionView): Promise<{ before: string; after: string } | null> {
  // Suggested edits apply to the page's current text; Poneva resolves them.
  // A suggested new page has no previous text: all of it is added.
  if (action.tool === 'suggest_page_edits') {
    const created = createdPageMarkdown(action.args);
    if (action.args.action === 'create')
      return created === null ? null : { before: '', after: created };
    const id = typeof action.args.pageId === 'string' ? action.args.pageId : null;
    if (!id || action.args.action !== 'edit' || !Array.isArray(action.args.edits)) return null;
    const preview = await knowledgePagesApi.preview(id, action.args.edits);
    return { before: preview.before, after: preview.after };
  }
  const after = actionText(action);
  if (!after) return null;
  if (action.tool === 'save_work_result') {
    const id = typeof action.args.replaces === 'string' ? action.args.replaces : null;
    if (!id) return null;
    const result = await workspaceApi.result(id);
    const payload = result.payload;
    const before =
      result.kind === 'markdown' && payload && 'markdown' in payload ? payload.markdown : null;
    return before === null ? null : { before, after };
  }
  if (action.tool === 'propose_knowledge_change') {
    const id = typeof action.args.pageId === 'string' ? action.args.pageId : null;
    const revision =
      typeof action.args.baseRevisionId === 'string' ? action.args.baseRevisionId : null;
    if (!id || !revision) return null;
    const base = await knowledgePagesApi.revision(id, revision);
    if (base.id !== revision) return null;
    return { before: base.markdown, after };
  }
  const id =
    typeof action.args.id === 'string'
      ? action.args.id
      : typeof action.args.skillId === 'string'
        ? action.args.skillId
        : null;
  if (!id) return null;
  const skill = await workspaceApi.skill(id);
  const revisions = await workspaceApi.revisions(id);
  const requested = typeof action.args.revision === 'number' ? action.args.revision : null;
  const previous =
    requested === null
      ? null
      : (revisions.find((revision) => revision.revision === requested) ??
        (skill.draft.revision === requested ? skill.draft : null) ??
        (skill.published?.revision === requested ? skill.published : null));
  if (!previous) return null;
  const candidate = {
    ...previous,
    ...(typeof action.args.name === 'string' ? { name: action.args.name } : {}),
    ...(typeof action.args.description === 'string'
      ? { description: action.args.description }
      : {}),
    ...(typeof action.args.instructions === 'string'
      ? { instructions: action.args.instructions }
      : {}),
  };
  return { before: snapshotText(previous), after: snapshotText(candidate) };
}

export function ActionDraftDiff({
  action,
  copy,
  onStats,
}: {
  action: ActionView;
  copy: ActionCopy;
  onStats?: (stats: { addedWords: number; removedWords: number } | null) => void;
}) {
  const diffCopy = documentDiffCopy(useChatInterfaceLocale());
  const supportsDiff = [
    'update_skill_draft',
    'save_work_result',
    'propose_knowledge_change',
    'suggest_page_edits',
  ].includes(action.tool);
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'error'; model?: LoadedDiff }>(
    () => ({
      status: supportsDiff ? 'loading' : 'ready',
    }),
  );
  // Polling may replace ActionView without changing the reviewed arguments.
  const identity = JSON.stringify([action.id, action.tool, action.args]);
  useEffect(() => {
    let active = true;
    setState({ status: supportsDiff ? 'loading' : 'ready' });
    onStats?.(null);
    if (!supportsDiff) {
      setState({ status: 'ready' });
      return () => {
        active = false;
      };
    }
    void loadPrevious(action)
      .then((value) => {
        if (!active) return;
        setState({
          status: 'ready',
          model: value ? diffDocuments(value.before, value.after) : undefined,
        });
      })
      .catch(() => {
        if (active) setState({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [identity]);
  useEffect(() => {
    if (state.status === 'ready')
      onStats?.(
        state.model
          ? { addedWords: state.model.addedWords, removedWords: state.model.removedWords }
          : null,
      );
  }, [onStats, state]);
  if (state.status === 'loading')
    return <p className="pc-action-diff-status">{diffCopy.loading}</p>;
  if (state.status === 'error')
    return (
      <p className="pc-action-diff-status" role="alert">
        {diffCopy.previousUnavailable}
      </p>
    );
  if (!state.model)
    return (
      <>
        {supportsDiff && <p className="pc-action-diff-status">{diffCopy.previousUnavailable}</p>}
        <ActionDraft action={action} copy={copy} />
      </>
    );
  if (!state.model.changedParagraphs)
    return <p className="pc-action-diff-status">{diffCopy.noChanges}</p>;
  return <DocumentDiffView model={state.model} />;
}
